import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { PageToolbar, FilterChip } from '@/components/ui/page-toolbar';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { FloatingField } from '@/components/ui/floating-field';
import { DateField } from '@/components/ui/date-field';
import { Combobox } from '@/components/ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatCurrency } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/** Hold new creates until the previous settlement finishes and appears in the list. */
const CREATE_BUFFER_MS = 2500;

const PAY_MODELS = [
  { value: 'percent', label: 'Percentage of Load' },
  { value: 'owner_op', label: 'Owner Operator' },
  { value: 'mileage', label: 'Per Mile' },
  { value: 'salary', label: 'Salary' },
];

function toYmd(d) {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function defaultPeriod() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return { start, end };
}

export default function SettlementsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [clearingId, setClearingId] = useState('');
  const [docGateHint, setDocGateHint] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createCooldownUntil, setCreateCooldownUntil] = useState(0);
  const createLockRef = useRef(false);
  const initialPeriod = defaultPeriod();
  const [form, setForm] = useState({
    driverId: '',
    payModel: 'percent',
    startDate: initialPeriod.start,
    endDate: initialPeriod.end,
    driverPct: '70',
    miles: '',
    perMile: '',
    salary: '',
    selectedTrips: [],
    autoIncludeExpenses: true,
    overridePod: false,
    deductions: [{ label: '', amount: '' }],
  });

  const resetCreateForm = () => {
    const period = defaultPeriod();
    setForm({
      driverId: '',
      payModel: 'percent',
      startDate: period.start,
      endDate: period.end,
      driverPct: '70',
      miles: '',
      perMile: '',
      salary: '',
      selectedTrips: [],
      autoIncludeExpenses: true,
      overridePod: false,
      deductions: [{ label: '', amount: '' }],
    });
    setDocGateHint(null);
  };

  const createBlocked = createBusy || Date.now() < createCooldownUntil;

  useEffect(() => {
    if (!createCooldownUntil) return undefined;
    const left = createCooldownUntil - Date.now();
    if (left <= 0) {
      setCreateCooldownUntil(0);
      return undefined;
    }
    const timer = setTimeout(() => setCreateCooldownUntil(0), left);
    return () => clearTimeout(timer);
  }, [createCooldownUntil]);

  const onlyReview = filter === 'review';

  const dataQ = useQuery({
    queryKey: ['settlements-page', onlyReview],
    queryFn: async () => {
      const q = onlyReview ? '?needsReview=true' : '';
      const [s, d, t] = await Promise.all([
        api(`/api/v1/settlements${q}`),
        api('/api/v1/drivers'),
        api('/api/v1/trips'),
      ]);
      if (s.status !== 200) throw new Error(s.data?.error ?? 'Failed to load settlements');
      if (d.status !== 200) throw new Error(d.data?.error ?? 'Failed to load drivers');
      if (t.status !== 200) throw new Error(t.data?.error ?? 'Failed to load trips');
      return {
        settlements: s.data.settlements ?? [],
        drivers: d.data.drivers ?? [],
        trips: t.data.trips ?? [],
      };
    },
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load settlements');
  }, [dataQ.isError, dataQ.error]);

  const settlements = useMemo(() => {
    const list = dataQ.data?.settlements ?? [];
    return [...list].sort((a, b) =>
      String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''))
    );
  }, [dataQ.data?.settlements]);
  const drivers = dataQ.data?.drivers ?? [];
  const trips = dataQ.data?.trips ?? [];

  const filtered =
    filter === 'paid'
      ? settlements.filter((s) => s.status === 'paid' || s.payRunId)
      : settlements;

  const defaults = defaultPeriod();
  const periodStart = toYmd(form.startDate) || toYmd(defaults.start);
  const periodEnd = toYmd(form.endDate) || toYmd(defaults.end);

  const tripsInRange = useMemo(() => {
    if (!form.driverId) return [];
    const start = new Date(`${periodStart}T00:00:00`).getTime();
    const end = new Date(`${periodEnd}T23:59:59`).getTime();
    return trips.filter((t) => {
      if (t.driverId !== form.driverId) return false;
      const ts = new Date(t.endedAt ?? t.startedAt ?? t.createdAt).getTime();
      return ts >= start && ts <= end;
    });
  }, [trips, form.driverId, periodStart, periodEnd]);

  const grossRevenue = useMemo(() => {
    const set = new Set(form.selectedTrips);
    return tripsInRange
      .filter((t) => set.has(t.id))
      .reduce((sum, t) => sum + (Number(t.revenue) || 0), 0);
  }, [tripsInRange, form.selectedTrips]);

  const parsedDeductions = form.deductions
    .filter((d) => d.label.trim() && d.amount !== '')
    .map((d) => ({ label: d.label.trim(), amount: Number(d.amount) || 0 }));

  const totalDeductions = parsedDeductions.reduce((s, d) => s + d.amount, 0);

  let previewGross = grossRevenue;
  if (form.payModel === 'mileage') {
    previewGross = (Number(form.miles) || 0) * (Number(form.perMile) || 0);
  } else if (form.payModel === 'salary') {
    previewGross = Number(form.salary) || 0;
  }

  const driverShare =
    form.payModel === 'percent'
      ? previewGross * ((Number(form.driverPct) || 0) / 100)
      : previewGross;
  const net = driverShare - totalDeductions;

  const toggleTrip = (id) => {
    setForm({
      ...form,
      selectedTrips: form.selectedTrips.includes(id)
        ? form.selectedTrips.filter((t) => t !== id)
        : [...form.selectedTrips, id],
    });
  };

  const addDeduction = () =>
    setForm({ ...form, deductions: [...form.deductions, { label: '', amount: '' }] });
  const updateDeduction = (idx, field, value) => {
    const d = [...form.deductions];
    d[idx] = { ...d[idx], [field]: value };
    setForm({ ...form, deductions: d });
  };
  const removeDeduction = (idx) =>
    setForm({ ...form, deductions: form.deductions.filter((_, i) => i !== idx) });

  const onSelectDriver = (id) => {
    const d = drivers.find((x) => x.id === id);
    const next = {
      ...form,
      driverId: id,
      selectedTrips: [],
    };
    if (d) {
      if (d.payModel) next.payModel = d.payModel;
      if (d.defaultDriverPct != null) next.driverPct = String(Math.round(d.defaultDriverPct * 100));
      if (d.defaultRatePerMile != null) next.perMile = String(d.defaultRatePerMile);
      if (d.defaultSalaryAmount != null) next.salary = String(d.defaultSalaryAmount);
      if (d.ownerOpFeeTemplate?.length) {
        next.deductions = d.ownerOpFeeTemplate.map((f) => ({
          label: f.label,
          amount: String(f.amount),
        }));
      }
    }
    setForm(next);
  };

  const pullExpenses = async () => {
    if (form.selectedTrips.length === 0) {
      toast.message('Select trips first');
      return;
    }
    const r = await api(
      `/api/v1/settlements/expense-preview?tripIds=${encodeURIComponent(form.selectedTrips.join(','))}`
    );
    if (r.status === 200 && r.data.deductions?.length) {
      setForm((prev) => ({
        ...prev,
        deductions: [
          ...prev.deductions.filter((d) => d.label.trim()),
          ...r.data.deductions.map((d) => ({ label: d.label, amount: String(d.amount) })),
        ],
      }));
      toast.success(`Added ${r.data.deductions.length} trip expense deduction(s)`);
    } else {
      toast.message('No trip expenses found');
    }
  };

  const handleCreate = async () => {
    // Lock synchronously on first click so rapid re-clicks cannot start another create.
    if (createLockRef.current) {
      toast.message('Wait for the current settlement to finish before creating another');
      return;
    }
    createLockRef.current = true;
    setCreateBusy(true);

    let holdLockMs = 0;
    let createdOk = false;
    try {
      if (Date.now() < createCooldownUntil) {
        toast.message('Wait for the current settlement to finish before creating another');
        return;
      }
      const driver = drivers.find((d) => d.id === form.driverId);
      if (!driver) {
        toast.error('Select a driver');
        return;
      }

      const r = await api('/api/v1/settlements', {
        method: 'POST',
        body: {
          driverId: driver.id,
          driverName: driver.name,
          periodStart,
          periodEnd,
          grossRevenue,
          driverPct: Number(form.driverPct) / 100,
          payModel: form.payModel,
          miles: form.payModel === 'mileage' ? Number(form.miles) || 0 : undefined,
          ratePerMile: form.payModel === 'mileage' ? Number(form.perMile) || 0 : undefined,
          salaryAmount: form.payModel === 'salary' ? Number(form.salary) || 0 : undefined,
          ownerOpFees:
            form.payModel === 'owner_op' && driver.ownerOpFeeTemplate?.length
              ? driver.ownerOpFeeTemplate
              : undefined,
          deductions: parsedDeductions,
          tripIds: form.selectedTrips,
          pullTripExpenses: form.autoIncludeExpenses || undefined,
          skipDocGate: form.overridePod || undefined,
        },
      });
      if (r.status === 201) {
        createdOk = true;
        toast.success('Settlement created');
        setDocGateHint(null);
        const period = defaultPeriod();
        setForm({
          driverId: '',
          payModel: 'percent',
          startDate: period.start,
          endDate: period.end,
          driverPct: '70',
          miles: '',
          perMile: '',
          salary: '',
          selectedTrips: [],
          autoIncludeExpenses: true,
          overridePod: false,
          deductions: [{ label: '', amount: '' }],
        });
        // Always show All so the new row is visible (Paid/Review filters can hide it).
        setFilter('all');
        qc.invalidateQueries({ queryKey: ['settlements'] });
        const created = r.data?.settlement;
        if (created) {
          const mergeList = (list) => {
            const rows = Array.isArray(list) ? list : [];
            const without = rows.filter((s) => s.id !== created.id);
            return [created, ...without];
          };
          const baseFrom = (prev) =>
            prev ?? {
              settlements: [],
              drivers: dataQ.data?.drivers ?? [],
              trips: dataQ.data?.trips ?? [],
            };
          // Write into the All-list cache even if it was empty/undefined.
          qc.setQueryData(['settlements-page', false], (prev) => {
            const base = baseFrom(prev);
            return { ...base, settlements: mergeList(base.settlements) };
          });
          if (created.needsReview && !created.reviewedAt) {
            qc.setQueryData(['settlements-page', true], (prev) => {
              const base = baseFrom(prev);
              return { ...base, settlements: mergeList(base.settlements) };
            });
          } else {
            qc.setQueryData(['settlements-page', true], (prev) => {
              if (!prev || !Array.isArray(prev.settlements)) return prev;
              return {
                ...prev,
                settlements: prev.settlements.filter((s) => s.id !== created.id),
              };
            });
          }
          await qc.invalidateQueries({ queryKey: ['settlements-page'] });
          try {
            await qc.refetchQueries({ queryKey: ['settlements-page', false] });
          } catch {
            /* ignore — cache already has the row */
          }
        }
        holdLockMs = CREATE_BUFFER_MS;
        setCreateCooldownUntil(Date.now() + CREATE_BUFFER_MS);
      } else if (r.status === 409 && r.data.code === 'DOC_PACKET_INCOMPLETE') {
        setDocGateHint(
          String(r.data.error ?? 'Missing POD on selected trip loads') +
            (r.data.hint ? ` — ${r.data.hint}` : '')
        );
        toast.error('Document packet incomplete — upload POD or override');
      } else {
        toast.error(String(r.data?.error ?? 'Create failed'));
      }
    } catch (err) {
      toast.error(err?.message ?? 'Create failed');
    } finally {
      setCreateBusy(false);
      if (createdOk) setShowCreate(false);
      if (holdLockMs > 0) {
        setTimeout(() => {
          createLockRef.current = false;
        }, holdLockMs);
      } else {
        createLockRef.current = false;
      }
    }
  };

  const clearReview = async (id) => {
    if (clearingId) return;
    setClearingId(id);
    try {
      const r = await api(`/api/v1/settlements/${id}/clear-review`, { method: 'POST', body: {} });
      if (r.status === 200 || r.status === 201) {
        toast.success('Review cleared — settlement is ready for pay runs');
        if (filter === 'review') setFilter('all');
        const updated = r.data?.settlement;
        if (updated) {
          qc.setQueryData(['settlements-page', false], (prev) => {
            if (!prev || !Array.isArray(prev.settlements)) return prev;
            return {
              ...prev,
              settlements: prev.settlements.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)),
            };
          });
          qc.setQueryData(['settlements-page', true], (prev) => {
            if (!prev || !Array.isArray(prev.settlements)) return prev;
            return {
              ...prev,
              settlements: prev.settlements.filter((s) => s.id !== updated.id),
            };
          });
        }
        qc.invalidateQueries({ queryKey: ['settlements'] });
        qc.invalidateQueries({ queryKey: ['pay-runs-page'] });
      } else {
        toast.error(String(r.data?.error ?? 'Failed'));
      }
    } finally {
      setClearingId('');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    const r = await api(`/api/v1/settlements/${id}`, { method: 'DELETE' });
    if (r.status === 200) {
      toast.success('Settlement deleted');
      setDeleteId('');
      qc.invalidateQueries({ queryKey: ['settlements'] });
      for (const key of [false, true]) {
        qc.setQueryData(['settlements-page', key], (prev) => {
          if (!prev || !Array.isArray(prev.settlements)) return prev;
          return {
            ...prev,
            settlements: prev.settlements.filter((s) => s.id !== id),
          };
        });
      }
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const columns = [
    { key: 'id', label: 'ID', className: 'font-mono', render: (v) => String(v).slice(0, 8) },
    { key: 'driverName', label: 'Driver', sortable: true },
    {
      key: 'period',
      label: 'Period',
      render: (_, row) => `${row.periodStart} → ${row.periodEnd}`,
    },
    {
      key: 'netAmount',
      label: 'Net Pay',
      render: (v) => <span className="font-mono font-bold">{formatCurrency(Number(v) || 0)}</span>,
    },
    {
      key: 'needsReview',
      label: 'Status',
      render: (_, row) =>
        row.needsReview && !row.reviewedAt ? (
          <StatusPill status="pending">{row.reviewReason || 'Needs review'}</StatusPill>
        ) : (
          <StatusPill status={row.status} />
        ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-1 justify-end">
          {row.needsReview && !row.reviewedAt ? (
            <Button
              size="sm"
              variant="outline"
              disabled={Boolean(clearingId)}
              onClick={() => clearReview(row.id)}
            >
              {clearingId === row.id ? 'Clearing…' : 'Clear Review'}
            </Button>
          ) : null}
          {!row.payRunId ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteId(row.id)}
              title="Delete settlement"
            >
              <Icons.Trash2 className="h-3 w-3" />
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const deleteTarget = settlements.find((s) => s.id === deleteId);

  const needsReviewCount = settlements.filter((s) => s.needsReview && !s.reviewedAt).length;

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Settlements"
          description="Driver settlement review and processing"
          icon="Wallet"
          metrics={[
            { label: 'Total', value: settlements.length },
            { label: 'Need Review', value: needsReviewCount },
            { label: 'Preview net', value: formatCurrency(net) },
          ]}
          actions={
            <Button
              disabled={createBlocked}
              onClick={() => {
                if (createBlocked) {
                  toast.message('Wait for the current settlement to finish before creating another');
                  return;
                }
                setShowCreate(!showCreate);
              }}
            >
              {createBusy ? (
                <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Icons.Plus className="h-4 w-4 mr-2" />
              )}
              {createBusy
                ? 'Creating…'
                : createCooldownUntil > Date.now()
                  ? 'Please wait…'
                  : 'New Settlement'}
            </Button>
          }
        />

        {createBlocked && !showCreate ? (
          <p className="text-xs text-muted-foreground">
            {createBusy
              ? 'Creating settlement — please wait until it appears in the list.'
              : 'Settlement just created — wait a moment before starting another.'}
          </p>
        ) : null}
        {needsReviewCount > 0 && (
          <Card className="border-l-4 border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10">
            <CardHeader className="flex flex-row items-start">
              <Icons.AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mr-3" />
              <div>
                <CardTitle className="text-base">Review required</CardTitle>
                <CardDescription className="mt-1">
                  {needsReviewCount} settlement(s) need your attention before processing pay runs
                </CardDescription>
              </div>
            </CardHeader>
          </Card>
        )}

        {showCreate && (
          <Card>
            <CardHeader>
              <CardTitle>New Settlement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Driver *
                  </label>
                  <Combobox
                    options={drivers.map((d) => ({
                      value: d.id,
                      label: `${d.name}${d.payModel ? ` (${d.payModel})` : ''}`,
                    }))}
                    value={form.driverId}
                    onChange={onSelectDriver}
                    placeholder="Select driver"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Pay Model *
                  </label>
                  <Select
                    value={form.payModel}
                    onValueChange={(v) => setForm({ ...form, payModel: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select pay model" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAY_MODELS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Period Start
                  </label>
                  <DateField
                    value={form.startDate}
                    onChange={(d) => setForm({ ...form, startDate: d })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Period End
                  </label>
                  <DateField
                    value={form.endDate}
                    onChange={(d) => setForm({ ...form, endDate: d })}
                  />
                </div>
              </div>

              {(form.payModel === 'percent' || form.payModel === 'owner_op') && (
                <FloatingField
                  label="Driver %"
                  type="number"
                  step="0.1"
                  value={form.driverPct}
                  onChange={(e) => setForm({ ...form, driverPct: e.target.value })}
                  disabled={form.payModel === 'owner_op'}
                />
              )}
              {form.payModel === 'mileage' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FloatingField
                    label="Miles"
                    type="number"
                    value={form.miles}
                    onChange={(e) => setForm({ ...form, miles: e.target.value })}
                  />
                  <FloatingField
                    label="$ per mile"
                    type="number"
                    step="0.01"
                    value={form.perMile}
                    onChange={(e) => setForm({ ...form, perMile: e.target.value })}
                  />
                </div>
              )}
              {form.payModel === 'salary' && (
                <FloatingField
                  label="Salary Amount ($)"
                  type="number"
                  step="0.01"
                  value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                />
              )}

              <div>
                <h4 className="font-semibold text-sm mb-2">Select Trips</h4>
                <div className="border rounded-lg divide-y max-h-60 overflow-auto">
                  {tripsInRange.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      No trips for this driver/range
                    </p>
                  ) : (
                    tripsInRange.map((t) => (
                      <label
                        key={t.id}
                        className="flex items-center gap-3 p-3 hover:bg-muted/30 cursor-pointer"
                      >
                        <Checkbox
                          checked={form.selectedTrips.includes(t.id)}
                          onCheckedChange={() => toggleTrip(t.id)}
                        />
                        <div className="flex-1 flex justify-between items-center gap-2">
                          <span className="font-mono text-sm">{t.id.slice(0, 8)}</span>
                          <span className="font-mono text-sm">
                            {formatCurrency(Number(t.revenue) || 0)}
                          </span>
                          <StatusPill status={t.status} />
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Deductions</h4>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={pullExpenses}>
                      Pull trip expenses
                    </Button>
                    <Button size="sm" variant="outline" onClick={addDeduction}>
                      <Icons.Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {form.deductions.map((d, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        value={d.label}
                        onChange={(e) => updateDeduction(idx, 'label', e.target.value)}
                        placeholder="Label (e.g., Fuel advance)"
                        className="flex-1 px-3 py-2 border rounded-md text-sm bg-background outline-none focus:ring-2 focus:ring-ring"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={d.amount}
                        onChange={(e) => updateDeduction(idx, 'amount', e.target.value)}
                        placeholder="Amount"
                        className="w-32 px-3 py-2 border rounded-md text-sm font-mono bg-background outline-none focus:ring-2 focus:ring-ring"
                      />
                      {form.deductions.length > 1 && (
                        <Button size="icon" variant="ghost" onClick={() => removeDeduction(idx)}>
                          <Icons.X className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.autoIncludeExpenses}
                    onCheckedChange={(c) => setForm({ ...form, autoIncludeExpenses: !!c })}
                  />
                  <span className="text-sm">Auto-include trip expenses</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.overridePod}
                    onCheckedChange={(c) => setForm({ ...form, overridePod: !!c })}
                  />
                  <span className="text-sm">Override missing POD requirement</span>
                </label>
              </div>

              {docGateHint && <p className="text-sm text-destructive">{docGateHint}</p>}

              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Gross revenue</span>
                  <span className="font-mono">{formatCurrency(grossRevenue)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Driver share</span>
                  <span className="font-mono">{formatCurrency(driverShare)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Deductions</span>
                  <span className="font-mono text-destructive">
                    -{formatCurrency(totalDeductions)}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Net Pay</span>
                  <span className="font-mono">{formatCurrency(net)}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={createBusy}
                  onClick={() => {
                    if (createBusy) return;
                    resetCreateForm();
                    setShowCreate(false);
                  }}
                >
                  Cancel
                </Button>
                {createBusy ? (
                  <div
                    className="inline-flex min-w-[200px] h-10 items-center justify-center gap-2 rounded-md border bg-muted px-4 text-sm font-medium"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <Icons.Loader2 className="h-4 w-4 animate-spin text-primary" />
                    Creating settlement…
                  </div>
                ) : (
                  <Button
                    type="button"
                    disabled={createBlocked || !form.driverId}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void handleCreate();
                    }}
                  >
                    Create Settlement
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <PageToolbar>
          {[
            { id: 'all', label: 'All' },
            { id: 'review', label: 'Needs Review' },
            { id: 'paid', label: 'Paid' },
          ].map((f) => (
            <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)}>
              {f.label}
            </FilterChip>
          ))}
        </PageToolbar>

        <DataTable
          columns={columns}
          data={filtered}
          emptyMessage={dataQ.isLoading ? 'Loading…' : 'No settlements'}
        />

        <ConfirmDialog
          open={Boolean(deleteId)}
          onOpenChange={(open) => !open && setDeleteId('')}
          title="Delete settlement?"
          description={`Remove this settlement for ${deleteTarget?.driverName || 'driver'}? This cannot be undone. Only allowed if not yet in a pay run.`}
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={handleDelete}
        />
      </div>
);
}
